import { useState, useMemo, useCallback } from "react";
import {
    Box,
    Divider,
    Paper,
    Typography,
    useTheme,
    Tabs,
    Tab,
    Switch,
    FormControlLabel,
    Theme,
    CircularProgress,
    Alert,
    Button,
    Tooltip,
} from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import { LineChart } from "@mui/x-charts/LineChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { FilterList } from "@mui/icons-material";
import { useFilterContext } from "../../../shared/context";
import { clientTheme } from "../../../theme/clientTheme";

const isModernTheme = clientTheme.layout?.portfolioCardVariant === "modern";




import { DataLayout, DonutChart, MetaDrivenTable, PaginationControls, StatusChip, DrawerControl, FilterSideBar } from "../../../shared/components";

import {
    useFetchMaterialLeadTimeStatistics,
    useFetchMaterialTrendStatistics,
    useFetchResourceLeadTimeStatistics,
    useFetchResourceTrendStatistics,
} from "../hooks/useFetchTrendStatistics";
import {
    useFetchMaterialDelayStatistics,
    useFetchResourceDelayStatistics,
} from "../hooks/useFetchDelayStatistics";
import {

    useFetchMaterialStatistics,
    useFetchResourceStatistics,
} from "../hooks/useFetchStatistics";
import { useGetMaterialReq } from "../../materialRequests/hooks/useGetMaterialReq";
import { useGetResourceReq } from "../../resourceRequests/hooks/useGetResourceReq";
import { useActiveFilterLabels } from "../../../shared/hooks";

// Types
interface VolumeData {
    week: string;
    materials: number;
    resources: number;
}

interface TrendData {
    period: string;
    materials_count: number;
    resources_count: number;
    materials_label: string;
    resources_label: string;
}

interface LeadTimeData {
    type: string;
    days: number;
}

interface KpiCardProps {
    title: string;
    value: string;
    subtitle: string;
    variance: string;
}

interface TabProps {
    isSwitched: boolean;
    onSwitchChange: () => void;
    theme: Theme;
    trendData: VolumeData[];
    isLoadingTrend: boolean;
    isLoadingLeadTime: boolean;
    donutData: DonutChartDataItem[];
    leadTimeData?: LeadTimeData[];
}

interface DonutChartDataItem {
    label: string;
    value: number;
    color: string;
    legendLabel: string;
    legendValue: string;
}

// Constants
const CHART_HEIGHTS = {
    TREND: 300,
    LEAD_TIME: 300,
    DONUT: 300,
} as const;

const LEAD_TIME_DATA: LeadTimeData[] = [
    { type: "Pipe", days: 9 },
    { type: "Valves", days: 12 },
    { type: "Fittings", days: 7 },
    { type: "Excavator", days: 4 },
    { type: "Labour", days: 3 },
    { type: "Specialist", days: 9 },
];

// Components
const KpiCard = ({ title, value, subtitle, variance }: KpiCardProps) => {
    return (
        <Paper
            elevation={3}
            sx={{ padding: 2, width: '100%' }}
            data-background-elevation={1}
            variant="outlined"
        >
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                <Typography variant="caption" color="text.secondary">
                    {title}
                </Typography>
                <Typography variant="body1" color="text.primary">
                    {value}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" color="text.secondary">
                        {subtitle}
                    </Typography>
                    <Typography variant="caption" color="success.main">
                        {variance}
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
};

const RequestVolumeTrend = ({ data, isLoading }: { data: VolumeData[]; isLoading: boolean }) => {
    const theme = useTheme();
    return (
        <LineChart
            xAxis={[{
                scaleType: 'point',
                data: data.map(d => d.week),
            }]}
            series={[
                {
                    data: data.map(d => d.materials),
                    label: 'Materials',
                    color: theme.palette.primary.main,
                },
                {
                    data: data.map(d => d.resources),
                    label: 'Resources',
                    color: theme.palette.success.main,
                },
            ]}
            height={CHART_HEIGHTS.TREND}
            margin={{ left: 0, right: 40, top: 20, bottom: 0 }}
            grid={{ vertical: true, horizontal: true }}
            slotProps={{
                legend: {
                    direction: 'horizontal',
                    position: { vertical: 'bottom', horizontal: 'center' },
                },
            }}
        />
    );
};

const AverageLeadTimeChart = ({ data }: { data: LeadTimeData[] }) => {
    const theme = useTheme();
    return (
        <BarChart
            xAxis={[{
                scaleType: 'band',
                data: data.map(d => d.type),
            }]}
            yAxis={[{
                label: 'Days',
            }]}
            series={[{
                data: data.map(d => d.days),
                color: theme.palette.primary.main,
            }]}
            height={CHART_HEIGHTS.LEAD_TIME}
            margin={{ left: 10, right: 30, top: 30, bottom: 10 }}
            grid={{ vertical: true, horizontal: true }}
        />
    );
};
// Tab Components
const MaterialRequestsTab = ({ isSwitched, onSwitchChange, theme, trendData, isLoadingTrend, donutData, leadTimeData }: TabProps) => {
    return (
        <Box sx={{ height: "100%", overflow: "auto", pr: 2 }}>
            <Box display="flex" gap={2}>
                <DataLayout title="Status Distribution">
                    <Box height={CHART_HEIGHTS.DONUT}>
                        <DonutChart data={donutData} />
                    </Box>
                </DataLayout>
                <DataLayout
                    bgVariant="filled"
                    title={isSwitched ? "Request Volume Trend" : "Average Lead Time by Type"}
                    action={
                        <FormControlLabel
                            sx={{ m: 0 }}
                            control={<Switch size="small" checked={isSwitched} onChange={onSwitchChange} />}
                            label={<Typography variant="caption">{isSwitched ? "Show Volume Trend" : "Show Lead Time"}</Typography>}
                        />
                    }
                >
                    {isSwitched ? (
                        <RequestVolumeTrend data={trendData} isLoading={isLoadingTrend} />
                    ) : (
                        <AverageLeadTimeChart data={leadTimeData || []} />
                    )}
                </DataLayout>
            </Box>
        </Box>
    );
};

const ResourceRequestsTab = ({ isSwitched, onSwitchChange, theme, trendData, isLoadingTrend, donutData, leadTimeData }: TabProps) => {
    return (
        <Box sx={{ height: "100%", overflow: "auto", pr: 2 }}>
            <Box display="flex" gap={2}>
                <DataLayout title="Status Distribution">
                    <Box height={CHART_HEIGHTS.DONUT}>
                        <DonutChart data={donutData} />
                    </Box>
                </DataLayout>
                <DataLayout
                    bgVariant="filled"
                    title={isSwitched ? "Request Volume Trend" : "Average Lead Time by Type"}
                    action={
                        <FormControlLabel
                            sx={{ m: 0 }}
                            control={<Switch size="small" checked={isSwitched} onChange={onSwitchChange} />}
                            label={<Typography variant="caption">{isSwitched ? "Show Volume Trend" : "Show Lead Time"}</Typography>}
                        />
                    }
                >
                    {isSwitched ? (
                        <RequestVolumeTrend data={trendData} isLoading={isLoadingTrend} />
                    ) : (
                        <AverageLeadTimeChart data={leadTimeData || []} />
                    )}
                </DataLayout>
            </Box>
        </Box>
    );
};

export const Dashboard = () => {
    const theme = useTheme();
    const { quickFilters } = useFilterContext();
    const { hasActiveFilters, activeFilterLabels } = useActiveFilterLabels();
    const [currentTab, setCurrentTab] = useState('material-requests');
    const [isMaterialSwitched, setIsMaterialSwitched] = useState(false);
    const [isResourceSwitched, setIsResourceSwitched] = useState(false);
    const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
    const handleOpenFilter = () => setIsOpenFilter(true);
    const handleCloseFilter = () => setIsOpenFilter(false);

    // Fetch trend statistics from APIs
    const {
        trendData: materialTrendData,
        isPending: isMaterialPending,
        isFetching: isMaterialFetching,
    } = useFetchMaterialTrendStatistics({ quickFilters });

    const {
        trendData: resourceTrendData,
        isPending: isResourcePending,
        isFetching: isResourceFetching,
    } = useFetchResourceTrendStatistics({ quickFilters });

    // add new lead time statistics
    const {
        leadTimeData: materialLeadTimeData,
        isPending: isMaterialLeadTimePending,
        isFetching: isMaterialLeadTimeFetching
    } = useFetchMaterialLeadTimeStatistics({ quickFilters });

    const {
        leadTimeData: resourceLeadTimeData,
        isPending: isResourceLeadTimePending,
        isFetching: isResourceLeadTimeFetching
    } = useFetchResourceLeadTimeStatistics({ quickFilters });

    // Fetch delay statistics from APIs
    const {
        delayedMaterials,
        activeMaterialRequests,
        materialsDelayedRate,
    } = useFetchMaterialDelayStatistics({ quickFilters });

    const {
        delayedResources,
        totalActiveResources,
        resourceDelayedRate,
    } = useFetchResourceDelayStatistics({ quickFilters });

    // Fetch statistics for donut charts
    const { statistics: materialStatistics } = useFetchMaterialStatistics({ quickFilters });
    const { statistics: resourceStatistics } = useFetchResourceStatistics({ quickFilters });

    const {
        data: materialRequests, isFetching: isMaterialDataFetching, isPending: isMaterialDataPending, isError: isMaterialError, error: materialError, pagination: materialPagination,
        changePage: changeMaterialPage, changeRowsPerPage: changeMaterialRowsPerPage
    } = useGetMaterialReq({ quickFilters });

    const materialRequestsHeaders = [
        { "alias_name": "material_ref", "label": "Material REF", "display_order": 1, is_show: true },
        { "alias_name": "type_display_text", "label": "Type", "display_order": 2, is_show: true },
        { "alias_name": "location", "label": "Location", "display_order": 3, is_show: true },
        { "alias_name": "point_of_contact_info", "label": "Point of Contact Info", "display_order": 6, is_show: true },
        { "alias_name": "status_display_text", "label": "Status", "display_order": 7, is_show: true },
        { "alias_name": "supplier_display_text", "label": "Supplier", "display_order": 8, is_show: true },
        { "alias_name": "required_date", "label": "Required Date", "display_order": 9, is_show: true },
        { "alias_name": "last_updated_comments", "label": "Last Updated Comments", "display_order": 10, is_show: true },
        { "alias_name": "activity_name", "label": "Activity Name", "display_order": 13, is_show: true },
        { "alias_name": "owner_name", "label": "Owner Name", "display_order": 14, is_show: true },
    ];

    const materialRequestsMetrics = {
        status_display_text: {
            type: 'component',
            element: ((row: { status_display_text: string; }) => (
                <StatusChip label={row.status_display_text} color={
                    row.status_display_text === 'Requested' ? 'default' :
                        row.status_display_text === 'Approved' ? 'blue' :
                            row.status_display_text === 'Ordered' ? 'amber' :
                                row.status_display_text === 'Delivered' ? 'green' :
                                    row.status_display_text === 'Used' ? 'violet' :
                                        'default'
                } />
            ))
        },
        order_placed_date: {
            type: 'date',
        },
        est_delivery_date: {
            type: 'date',
        },
        required_date: {
            type: 'date',
        }
    };

    const {
        data: resourceRequests, isFetching: isResourceDataFetching, isPending: isResourceDataPending, isError: isResourceError, error: resourceError, pagination: resourcePagination,
        changePage: changeResourcePage, changeRowsPerPage: changeResourceRowsPerPage
    } = useGetResourceReq({ quickFilters });

    const resourceRequestsHeaders = [
        { "alias_name": "resource_ref", "label": "Resource REF", "display_order": 1, is_show: true },
        { "alias_name": "resource_type_display_text", "label": "Type", "display_order": 2, is_show: true },
        { "alias_name": "description", "label": "Description", "display_order": 3, is_show: true },
        { "alias_name": "status_display_text", "label": "Status", "display_order": 7, is_show: true },
        { "alias_name": "resource_supplier_display_text", "label": "Supplier", "display_order": 8, is_show: true },
        { "alias_name": "required_date", "label": "Required Date", "display_order": 9, is_show: true },
        { "alias_name": "comments", "label": "Comments", "display_order": 10, is_show: true },
        { "alias_name": "project_title", "label": "Project Title", "display_order": 11, is_show: true },
        { "alias_name": "phase_name", "label": "Phase Name", "display_order": 12, is_show: true },
        { "alias_name": "activity_name", "label": "Activity Name", "display_order": 13, is_show: true },
    ];

    const resourceRequestsMetrics = {
        status_display_text: {
            type: 'component',
            element: ((row: { status_display_text: string; }) => (
                <StatusChip label={row.status_display_text} color={
                    row.status_display_text === 'Requested' ? 'default' :
                        row.status_display_text === 'Approved' ? 'blue' :
                            row.status_display_text === 'Ordered' ? 'amber' :
                                row.status_display_text === 'Delivered' ? 'green' :
                                    row.status_display_text === 'Used' ? 'violet' :
                                        'default'
                } />
            ))
        },
        order_placed_date: {
            type: 'date',
        },
        est_delivery_date: {
            type: 'date',
        },
        required_date: {
            type: 'date',
        }
    };

    // Transform API data to chart format with memoization
    const materialVolumeTrendData = useMemo<VolumeData[]>(
        () =>
            materialTrendData.map((item: TrendData) => ({
                week: item.period,
                materials: item.materials_count,
                resources: item.resources_count,
            })),
        [materialTrendData]
    );

    const resourceVolumeTrendData = useMemo<VolumeData[]>(
        () =>
            resourceTrendData.map((item: TrendData) => ({
                week: item.period,
                materials: item.materials_count,
                resources: item.resources_count,
            })),
        [resourceTrendData]
    );

    // Event handlers
    const handleChangeTab = (_e: React.SyntheticEvent, tab: string) => {
        setCurrentTab(tab);
    };

    const toggleMaterialSwitch = useCallback(() => {
        setIsMaterialSwitched((prev) => !prev);
    }, []);

    const toggleResourceSwitch = useCallback(() => {
        setIsResourceSwitched((prev) => !prev);
    }, []);

    // Transform statistics to donut chart format
    const materialDonutData = useMemo<DonutChartDataItem[]>(() => {
        const colors = [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main, theme.palette.warning.main, theme.palette.error.main, theme.palette.info.main];
        return materialStatistics.map((item, index) => ({
            label: item.status_display_text,
            value: item.percentage,
            color: colors[index % colors.length],
            legendLabel: `${item.status_display_text} - ${item.count}`,
            legendValue: `${item.percentage}%`,
        }));
    }, [materialStatistics, theme]);

    const resourceDonutData = useMemo<DonutChartDataItem[]>(() => {
        const colors = [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main, theme.palette.warning.main, theme.palette.error.main, theme.palette.info.main];
        return resourceStatistics.map((item, index) => ({
            label: item.status_display_text,
            value: item.percentage,
            color: colors[index % colors.length],
            legendLabel: `${item.status_display_text} - ${item.count}`,
            legendValue: `${item.percentage}%`,
        }));
    }, [resourceStatistics, theme]);

    // Generate KPI cards based on API data
    const materialKpiCards = useMemo<KpiCardProps[]>(
        () => [
            {
                title: "Delayed Materials",
                value: String(delayedMaterials),
                subtitle: `${activeMaterialRequests} active requests`,
                variance: `${(materialsDelayedRate * 100).toFixed(1)}% delay rate`,
            },
            {
                title: "Active Material Requests",
                value: String(activeMaterialRequests),
                subtitle: `${delayedMaterials} delayed`,
                variance: `${(materialsDelayedRate * 100).toFixed(1)}% of total`,
            },
            {
                title: "Materials Delayed Rate",
                value: `${(materialsDelayedRate * 100).toFixed(1)}%`,
                subtitle: `${delayedMaterials} delayed out of ${activeMaterialRequests}`,
                variance: materialsDelayedRate > 0.2 ? "High delay rate" : "Acceptable delay rate",
            }
        ],
        [delayedMaterials, activeMaterialRequests, materialsDelayedRate]
    );

    const resourceKpiCards = useMemo<KpiCardProps[]>(
        () => [
            {
                title: "Delayed Resources",
                value: String(delayedResources),
                subtitle: `${totalActiveResources} active resources`,
                variance: `${(resourceDelayedRate * 100).toFixed(1)}% delay rate`,
            },
            {
                title: "Total Active Resources",
                value: String(totalActiveResources),
                subtitle: `${delayedResources} delayed`,
                variance: `${(resourceDelayedRate * 100).toFixed(1)}% of total`,
            },
            {
                title: "Resources Delayed Rate",
                value: `${(resourceDelayedRate * 100).toFixed(1)}%`,
                subtitle: `${delayedResources} delayed out of ${totalActiveResources}`,
                variance: resourceDelayedRate > 0.2 ? "High delay rate" : "Acceptable delay rate",
            }
        ],
        [delayedResources, totalActiveResources, resourceDelayedRate]
    );

    if (isMaterialDataFetching || isMaterialDataPending || isResourceDataFetching || isResourceDataPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    if (isMaterialError || isResourceError) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
            >
                <Alert
                    severity="warning"
                    sx={{
                        width: "auto",
                        justifyContent: "center",
                        textAlign: "center",
                    }}
                >
                    {materialError?.message || resourceError?.message || "Something went wrong!"}
                </Alert>
            </Box>
        );
    };

    return (
        <Box display={'flex'} flexDirection={'column'} height={"100%"} overflow={"hidden"} pb={2}>
            {/* Tabs Navigation */}
            <TabContext value={currentTab}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Tabs
                        value={currentTab}
                        onChange={handleChangeTab}
                        aria-label="Tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{ minHeight: "auto", flex: 1 }}
                    >
                        <Tab value="material-requests" label="Material Requests" />
                        <Tab value="resource-requests" label="Resource Requests" />
                    </Tabs>
                     <Tooltip
                  title={
                hasActiveFilters ? (
                <Box sx={{ p: 0.5 }}>
                  {activeFilterLabels.map((label, i) => (
                    <Typography key={i} variant="caption" display="block">
                      {label}
                    </Typography>
                  ))}
                </Box>
              ) : ""
            }
            placement="bottom-start"
            disableHoverListener={!hasActiveFilters}
            arrow
          >
            <Button
              startIcon={<FilterList />}
              variant={isModernTheme ? "outlined" : (hasActiveFilters ? "contained" : "text")}
              color="inherit"
              onClick={handleOpenFilter}
              sx={
                isModernTheme
                  ? {
                    marginLeft: "auto",
                    borderRadius: 2,
                    textTransform: "none",
                    borderColor: "divider",
                    color: hasActiveFilters ? "primary.main" : "text.primary",
                    whiteSpace: "nowrap",
                  }
                  : hasActiveFilters
                  ? {
                    marginLeft: "auto",
                    backgroundColor: "action.selected",
                    color: "text.primary",
                    "&:hover": { backgroundColor: "action.hover" },
                  }
                  : { marginLeft: "auto" }
              }
            >
              {isModernTheme ? `Filters${hasActiveFilters ? ` (${activeFilterLabels.length})` : ""}` : `FILTERS${hasActiveFilters ? ` (${activeFilterLabels.length})` : ""}`}
            </Button>
          </Tooltip>
            </Box>
                <Divider />
                <Box flex={1} overflow="hidden" height="100%" sx={{ overflowY: 'auto' }} p={2}>
                    <TabPanel value="material-requests" sx={{ p: 0, height: "100%" }}>
                        <Box display="flex" flexDirection="column" gap={2} mb={2}>
                            <Box display="flex" justifyContent="space-between" gap={2}>
                                {materialKpiCards.map((kpi, index) => (
                                    <KpiCard key={index} {...kpi} />
                                ))}
                            </Box>
                            <MaterialRequestsTab
                                isSwitched={isMaterialSwitched}
                                onSwitchChange={toggleMaterialSwitch}
                                theme={theme}
                                trendData={materialVolumeTrendData}
                                isLoadingTrend={isMaterialPending || isMaterialFetching}
                                donutData={materialDonutData}
                                leadTimeData={materialLeadTimeData}
                                isLoadingLeadTime={isMaterialLeadTimePending || isMaterialLeadTimeFetching}
                            />
                            <Paper elevation={3} variant="outlined" sx={{ overflow: "hidden" }}>
                                <MetaDrivenTable headers={materialRequestsHeaders} data={materialRequests} dataMetrics={materialRequestsMetrics} sortDisabled />
                                <PaginationControls
                                    total={materialPagination?.total}
                                    rowsPerPage={materialPagination?.limit}
                                    currentPage={materialPagination?.page}
                                    handlePageChange={changeMaterialPage}
                                    handleRowsPerPage={changeMaterialRowsPerPage}
                                />
                            </Paper>
                        </Box>
                    </TabPanel>
                    <TabPanel value="resource-requests" sx={{ p: 0, height: "100%" }}>
                        <Box display="flex" flexDirection="column" gap={2} mb={2}>
                            <Box display="flex" justifyContent="space-between" gap={2}>
                                {resourceKpiCards.map((kpi, index) => (
                                    <KpiCard key={index} {...kpi} />
                                ))}
                            </Box>
                            <ResourceRequestsTab
                                isSwitched={isResourceSwitched}
                                onSwitchChange={toggleResourceSwitch}
                                theme={theme}
                                trendData={resourceVolumeTrendData}
                                isLoadingTrend={isResourcePending || isResourceFetching}
                                donutData={resourceDonutData}
                                leadTimeData={resourceLeadTimeData}
                                isLoadingLeadTime={isResourceLeadTimePending || isResourceLeadTimeFetching}
                            />
                            <Paper elevation={3} variant="outlined" sx={{ overflow: "hidden" }}>
                                <MetaDrivenTable headers={resourceRequestsHeaders} data={resourceRequests} dataMetrics={resourceRequestsMetrics} sortDisabled />
                                <PaginationControls
                                    total={resourcePagination?.total}
                                    rowsPerPage={resourcePagination?.limit}
                                    currentPage={resourcePagination?.page}
                                    handlePageChange={changeResourcePage}
                                    handleRowsPerPage={changeResourceRowsPerPage}
                                />
                            </Paper>
                        </Box>
                    </TabPanel>
                </Box>
            </TabContext>
            <DrawerControl
                title="Filter Records"
                subTitle="Please apply following filters to refine results"
                isOpen={isOpenFilter}
                handleClose={handleCloseFilter}
            >
                <FilterSideBar hiddenFilters={["stakeholder"]} onClose={handleCloseFilter} />
            </DrawerControl>

        </Box>
    );
};